import { type KidInfo } from '@fruitful/member-profile';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodyLargeEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledInput,
    Form,
    H5Regular,
    USE_FORM_MODE_DEFAULTS,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';

const addKidSchema = z.object({
    firstName: z.string().optional(),
    birthYear: z
        .string()
        .length(4, 'Birth year must be 4 digits')
        .regex(/^\d{4}$/, 'Birth year must be a valid year'),
});

type AddKidSchema = z.infer<typeof addKidSchema>;

interface AddKidModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (newKid: KidInfo) => Promise<void>;
}

export const AddKidModal = ({
    isOpen,
    onClose,
    onSubmit,
}: AddKidModalProps) => {
    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
    } = useForm<AddKidSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(addKidSchema),
        defaultValues: { firstName: '', birthYear: '' },
    });

    const onFormSubmit: SubmitHandler<AddKidSchema> = async ({
        firstName,
        birthYear,
    }) => {
        const newKid: KidInfo = {
            firstName,
            birthYear,
        };

        await onSubmit(newKid);
        reset();
        onClose();
    };

    return (
        <GroveSimpleModal
            isOpen={isOpen}
            hasCloseButton={true}
            onClose={onClose}
            renderInAppRoot={false}
        >
            <YStack
                marginHorizontal={16}
                marginTop={32}
                width={600}
                gap={24}
                marginBottom={24}
            >
                <H5Regular>Add New Kid</H5Regular>
                <Form onSubmit={handleSubmit(onFormSubmit)}>
                    <YStack gap={20}>
                        <YStack gap={16}>
                            <BodyLargeEmphasized>
                                Kid Details
                            </BodyLargeEmphasized>
                            <ControlledInput
                                name="firstName"
                                label="First name (optional)"
                                control={control}
                                register={register}
                                errors={errors}
                            />
                            <ControlledInput
                                name="birthYear"
                                label="Birth year"
                                placeholder="YYYY"
                                control={control}
                                register={register}
                                errors={errors}
                            />
                        </YStack>

                        <YStack gap={12} justifyContent="flex-end">
                            <Button
                                disabled={!isValid}
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.SMALL}
                                text="Save"
                                onPress={handleSubmit(onFormSubmit)}
                            />
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.SMALL}
                                text="Cancel"
                                onPress={onClose}
                            />
                        </YStack>
                    </YStack>
                </Form>
            </YStack>
        </GroveSimpleModal>
    );
};
